/**
 * Mock Express module for browser environment
 * This provides a simple implementation of the Express API for a game's server code running in a browser page
 */

export class MockExpressResponse {
  headers: Record<string, string>;
  statusCode: number;
  body: any;
  
  constructor() {
    this.headers = {};
    this.statusCode = 200;
    this.body = null;
  }

  status(code: number) {
    this.statusCode = code;
    return this;
  }

  json(data: any) {
    this.body = data;
    this.headers['Content-Type'] = 'application/json';
    return this;
  }

  send(data: any) {
    this.body = data;
    return this;
  }

  setHeader(name: string, value: string) {
    this.headers[name] = value;
    return this;
  }

  end(data?: any) {
    if (data) {
      this.body = data;
    }
    return this;
  }
}

export class MockExpressRequest {
  method: string;
  url: string;
  headers: Record<string, string>;
  body: any;
  query: Record<string, any>;
  params: Record<string, any>;
  
  constructor(options: any = {}) {
    this.method = options.method || 'GET';
    this.url = options.url || '/';
    this.headers = options.headers || {};
    this.body = options.body || {};
    this.query = options.query || {};
    this.params = options.params || {};
  }
}

export class MockExpressRouter {
  routes: {
    method: string;
    path: string;
    handler: Function;
  }[] = [];

  get(path: string, handler: Function) {
    this.routes.push({ method: 'GET', path, handler });
    return this;
  }

  post(path: string, handler: Function) {
    this.routes.push({ method: 'POST', path, handler });
    return this;
  }

  put(path: string, handler: Function) {
    this.routes.push({ method: 'PUT', path, handler });
    return this;
  }

  delete(path: string, handler: Function) {
    this.routes.push({ method: 'DELETE', path, handler });
    return this;
  }

  use(path: string | Function, handler?: Function) {
    if (typeof path === 'function') {
      this.routes.push({ method: 'USE', path: '*', handler: path });
    } else if (handler) {
      this.routes.push({ method: 'USE', path, handler });
    }
    return this;
  }
}

export class MockExpress {
  private routes: {
    method: string;
    path: string;
    handler: Function;
  }[] = [];
  private middleware: Function[] = [];

  constructor() {
    // No initialization needed
  }

  get(path: string, handler: Function) {
    this.routes.push({ method: 'GET', path, handler });
    return this;
  }

  post(path: string, handler: Function) {
    this.routes.push({ method: 'POST', path, handler });
    return this;
  }

  put(path: string, handler: Function) {
    this.routes.push({ method: 'PUT', path, handler });
    return this;
  }

  delete(path: string, handler: Function) {
    this.routes.push({ method: 'DELETE', path, handler });
    return this;
  }

  use(path: string | Function, handler?: Function) {
    if (typeof path === 'function') {
      this.middleware.push(path);
    } else if (handler) {
      this.routes.push({ method: 'USE', path, handler });
    }
    return this;
  }

  listen(port: number, callback?: Function) {
    console.log(`Mock Express server listening on port ${port}`);
    if (callback) {
      callback();
    }
    return {
      close: () => console.log(`Mock Express server on port ${port} closed`)
    };
  }

  static Router() {
    return new MockExpressRouter();
  }

  static static(path: string) {
    return (req: any, res: any, next: Function) => {
      console.log(`Serving static files from ${path}`);
      next();
    };
  }

  static json() {
    return (req: any, res: any, next: Function) => {
      console.log('JSON body parser middleware');
      next();
    };
  }

  static urlencoded() {
    return (req: any, res: any, next: Function) => {
      console.log('URL encoded body parser middleware');
      next();
    };
  }
}

// Factory function to create a new Express app instance
export function createExpressApp(): MockExpress {
  return new MockExpress();
}

export default createExpressApp;
